import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { Entry, Fixture, Round } from '@h2h/shared';
import { SCREEN_COLUMN } from '../../components/ui/AppShell';
import { cn } from '../../lib/cn';
import { identityFor } from '../../data/leagues';
import { getOverlayRoot, useOverlayChromeLock } from '../../lib/overlayRoot';
import { EASE_OUT_IOS, SHEET_DISMISS, SPRING_SHEET, prefersReducedMotion, useIsDesktop } from '../../lib/motion';
import { useFixtureInsight, useLeagues } from '../../hooks/useQueries';
import { BackBar } from './BackBar';
import { FootballMatchView } from './FootballMatchView';
import { useFootballMatchDetailVM } from './useFootballMatchDetail';

const SHELL_DARK = 'bg-[linear-gradient(179deg,#1A1A22_0%,#0C0C11_58%,#08080C_100%)]';

/**
 * The live / settled match view as a slide-up overlay over the league hub, instead
 * of a full route push. Keeping the hub mounted underneath means opening and closing
 * is just this sheet animating — no screen remount, no tab re-enter, no light↔dark
 * page seam — so going in and back stays buttery (the same surface the card builder
 * uses). Reuses the exact match-detail view via FootballMatchView's embedded mode.
 */
export function LiveFixtureSheet({
  openFixtureId,
  onClose,
  leagueId,
  round,
  fixtures,
  entry,
  nationalTeams
}: {
  openFixtureId: string | null;
  onClose: () => void;
  leagueId: string;
  round: Round;
  fixtures: Fixture[];
  entry: Entry | null | undefined;
  nationalTeams: boolean;
}) {
  const reduce = prefersReducedMotion();
  const wide = useIsDesktop();
  const [raised, setRaised] = useState(false);
  const [exiting, setExiting] = useState(false);
  // Retain the last fixture so its content stays rendered through the close slide.
  const [shownId, setShownId] = useState<string | null>(null);
  useEffect(() => {
    if (openFixtureId) {
      setShownId(openFixtureId);
      setExiting(false);
    } else if (shownId != null) {
      setExiting(true);
    }
  }, [openFixtureId, shownId]);

  const open = !!openFixtureId;
  const fixtureId = openFixtureId ?? shownId;
  const fixture = useMemo(
    () => fixtures.find((f) => f.fixtureId === fixtureId),
    [fixtures, fixtureId]
  );

  useOverlayChromeLock(open || exiting);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Paint the off-screen sheet once, then raise it next frame (prepare-then-animate)
  // so the heavy match view's first paint never lands on the slide frame.
  useEffect(() => {
    if (!open) {
      setRaised(false);
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setRaised(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence
      initial={false}
      onExitComplete={() => {
        setExiting(false);
        setShownId(null);
      }}
    >
      {open && fixture && wide && (
        <motion.div
          key="live-fixture-backdrop"
          aria-hidden
          onClick={onClose}
          className="pointer-events-auto fixed inset-0 bg-black/55 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: raised ? 1 : 0 }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease: EASE_OUT_IOS } }}
        />
      )}
      {open && fixture && (
        <motion.div
          key="live-fixture-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Live match"
          className={cn(
            'pointer-events-auto fixed flex flex-col',
            SHELL_DARK,
            wide
              ? 'right-0 top-0 bottom-0 w-[min(400px,46vw)] shadow-[-26px_0_70px_-34px_rgba(0,0,0,0.7)] lg:w-[min(540px,96vw)]'
              : 'inset-0 w-full pt-safe'
          )}
          initial={reduce ? { opacity: 0 } : wide ? { x: '100%' } : { y: '100%' }}
          animate={
            reduce
              ? { opacity: raised ? 1 : 0 }
              : wide
                ? { x: raised ? 0 : '100%' }
                : { y: raised ? 0 : '100%' }
          }
          exit={
            reduce
              ? { opacity: 0 }
              : wide
                ? { x: '100%', transition: SHEET_DISMISS }
                : { y: '100%', transition: SHEET_DISMISS }
          }
          transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_SHEET}
          style={{ willChange: 'transform' }}
        >
          <div className={cn('flex min-h-0 flex-1 flex-col', wide ? 'px-4' : SCREEN_COLUMN)}>
            <div className="native-scroll no-scrollbar min-h-0 flex-1 overflow-y-auto pb-8">
              <LiveFixtureContent
                fixture={fixture}
                fixtures={fixtures}
                entry={entry}
                round={round}
                leagueId={leagueId}
                nationalTeams={nationalTeams}
                onClose={onClose}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}

function LiveFixtureContent({
  fixture,
  fixtures,
  entry,
  round,
  leagueId,
  nationalTeams,
  onClose
}: {
  fixture: Fixture;
  fixtures: Fixture[];
  entry: Entry | null | undefined;
  round: Round;
  leagueId: string;
  nationalTeams: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: leagues } = useLeagues();
  const { data: insight } = useFixtureInsight(leagueId, round.roundId, fixture.fixtureId);

  const leagueName =
    leagues?.find((l) => l.leagueId === leagueId)?.name ?? identityFor(leagueId)?.name ?? 'League';
  const myPick = entry?.predictions.find((p) => p.fixtureId === fixture.fixtureId);

  const goBoard = () => {
    onClose();
    navigate(`/play/${leagueId}/waiting?round=${round.roundId}`);
  };

  const vm = useFootballMatchDetailVM({
    fixture,
    fixtures,
    entry: entry ?? undefined,
    round,
    myPick,
    nationalTeams,
    goPicks: onClose,
    goCompete: onClose
  });

  return (
    <>
      <BackBar onBack={onClose} eyebrow={`${leagueName} · ${round.label ?? 'Fixture'}`} />
      <FootballMatchView
        embedded
        fixture={fixture}
        round={round}
        leagueName={leagueName}
        myPick={myPick}
        vm={vm}
        insight={insight}
        onBack={onClose}
        onLiveBoard={goBoard}
      />
    </>
  );
}
